import { useMemo, useState, useCallback } from "react";
import {
  FiAlertOctagon, FiAlertTriangle, FiInfo, FiLayers,
  FiUsers, FiWifiOff, FiTruck, FiTool,
} from "react-icons/fi";
import useAuth from "../../../Hooks/useAuth";
import usePersistedFilters from "../../../Hooks/usePersistedFilters";
import useAlerts from "../../../Hooks/storageRoles/useAlerts";
import { PageShell, PageHeader, FilterTabs, EmptyState } from "../../../Components/shared";
import { AlertBellIcon } from "../../../Components/atoms/animatedIcons";
import AlertCategoryGrid, {
  AlertCategoryGridSkeleton,
} from "../../../Components/Alerts/storageRoles/AlertCategoryGrid";
import AlertsLiveBadge from "../../../Components/Alerts/storageRoles/AlertsLiveBadge";
import AlertsAllClearBanner from "../../../Components/Alerts/storageRoles/AlertsAllClearBanner";
import AlertDrawer from "../../../Components/Dashboard/storageRoles/AlertDrawer";
import {
  buildAlertCategories,
  summarizeAlerts,
  pendingAlertGroups,
} from "../../../utils/alertCategories";

// PageHeader ikonu bileşen olarak alır; satır içi fonksiyon her render'da
// yeni bileşen olur, ikon yeniden mount edilip animasyonu baştan başlardı.
const HeaderBell = ({ className }) => <AlertBellIcon className={className} />;
const HeaderBellActive = ({ className }) => <AlertBellIcon className={className} active />;

/**
 * Uyarılar sayfası (#229) — menüde DASHBOARD grubunun altında.
 * Veri: useAlerts (socket, canlı). Kategori kartı → mevcut AlertDrawer.
 * Görünen kategoriler sunucudaki alerts.devices / firms / orders izinlerine
 * göre gelir; izni olmayan dilim hiç gönderilmez.
 */
export default function AlertsPage() {
  const { auth } = useAuth();
  const { data, isLoading, isConnected, error } = useAlerts(auth?.accessToken);
  const [filters, setFilters] = usePersistedFilters("alerts", { severity: "all" });
  const [drawer, setDrawer] = useState(null);

  const categories = useMemo(() => buildAlertCategories(data), [data]);
  const summary = useMemo(() => summarizeAlerts(categories), [categories]);
  // Kademeli gönderim (#238): henüz hesaplanan gruplar iskelet olarak durur
  const pendingGroups = useMemo(() => pendingAlertGroups(data), [data]);

  const visible = useMemo(
    () =>
      filters.severity === "all"
        ? categories
        : categories.filter((c) => c.severity === filters.severity),
    [categories, filters.severity]
  );

  const openCategory = useCallback((category) => {
    setDrawer({
      title: category.drawerTitle,
      dataType: category.dataType,
      data: category.items,
    });
  }, []);

  // Başlık sayaçları (#269): cihaz dilimi açıksa konum + uyarı sayıları
  // (sözleşme v1.5 devices.counts); değilse önem derecesi özeti.
  const devices = data?.devices;
  const countOr = (value) => (value == null ? "–" : value);
  const stats = data?.access?.devices
    ? [
        { label: "Müşteride", value: countOr(devices?.counts?.customer), icon: FiUsers, color: "emerald" },
        { label: "Bağlı olmayan", value: countOr(devices?.unConnected?.length), icon: FiWifiOff, color: "amber" },
        { label: "Kritik kota", value: countOr(devices?.critical?.length), icon: FiAlertOctagon, color: "red" },
        { label: "Plasiyerde", value: countOr(devices?.counts?.playmaker), icon: FiTruck, color: "blue" },
        { label: "Serviste", value: countOr(devices?.counts?.service), icon: FiTool, color: "indigo" },
      ]
    : [
        { label: "Toplam", value: summary.total, icon: FiLayers, color: "indigo" },
        { label: "Kritik", value: summary.danger, icon: FiAlertOctagon, color: "red" },
        { label: "Uyarı", value: summary.warning, icon: FiAlertTriangle, color: "amber" },
      ];

  const tabs = [
    { id: "all", label: "Tümü", icon: FiLayers, count: summary.total },
    { id: "danger", label: "Kritik", icon: FiAlertOctagon, count: summary.danger },
    { id: "warning", label: "Uyarı", icon: FiAlertTriangle, count: summary.warning },
    { id: "info", label: "Bilgi", icon: FiInfo, count: summary.info },
  ];

  const renderBody = () => {
    if (isLoading) return <AlertCategoryGridSkeleton />;
    if (error && !data) {
      return <EmptyState message="Uyarılar yüklenemedi" suggestion={error} />;
    }
    if (!categories.length && !pendingGroups.length) {
      return (
        <EmptyState
          message="Görüntüleyebileceğiniz uyarı yok"
          suggestion="Uyarı türlerini görmek için yöneticinizden cihaz, firma veya sipariş uyarısı izni isteyin."
        />
      );
    }
    if (!visible.length && !pendingGroups.length) {
      return <EmptyState message="Bu filtrede kategori yok" />;
    }
    return (
      <>
        {summary.total === 0 && !pendingGroups.length && <AlertsAllClearBanner />}
        <AlertCategoryGrid categories={visible} onOpen={openCategory} pendingGroups={pendingGroups} />
      </>
    );
  };

  return (
    <PageShell>
      <div className="border-b border-gray-100 p-3 sm:p-4">
        <PageHeader
          icon={summary.danger > 0 ? HeaderBellActive : HeaderBell}
          iconColor="amber"
          title="Uyarılar"
          subtitle="Dikkat edilmesi gereken cihaz ve firmalar"
          stats={stats}
          isLoading={isLoading}
          slot={<AlertsLiveBadge isConnected={isConnected} generatedAt={data?.generatedAt} />}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4">
        {(categories.length > 0 || pendingGroups.length > 0) && (
          <FilterTabs
            filters={tabs}
            activeFilter={filters.severity}
            onChange={(severity) => setFilters((prev) => ({ ...prev, severity }))}
            totalCount={tabs.find((t) => t.id === filters.severity)?.count}
            isLoading={isLoading}
          />
        )}
        {renderBody()}
      </div>

      <AlertDrawer alertDrawer={drawer} onClose={() => setDrawer(null)} />
    </PageShell>
  );
}
